import { ConversionError } from '@shared/excel/converters';
import { RangeAddress } from '@shared/excel/range-address';
import { AddressResolver, CellFormatter, ValueConverter } from '@shared/execution/types';
import { WorkbookApi, WorkbookApiFactory } from '@shared/execution/workbook-api';
import { EventBus } from '@shared/infrastructure/app-event-bus';
import { ProviderClient, ProviderEvents, ProviderFactory, ProviderRemoteChunkEvent } from '@shared/providers/provider';
import {
  InsertRecordError,
  isInsertRecordError,
  isMetadata,
  MetadataResult,
  PushObjectRecord,
  STREAM_METADATA,
  UpdateResult,
} from '@shared/providers/provider-transport';
import { DataField } from '@shared/providers/types';
import { Flowstep } from '@shared/types/flowstep';
import { ConstantField, MappedHeader } from '@shared/types/flowstep/mapping';
import { CancelError, CancelToken } from '@shared/utils/cancelToken';
import { NoDataError, RangeType, ResultDataError } from '@shared/utils/flowstep';
import dayjs from 'dayjs';
import { CellAddressPickerValue } from 'frontend/excel365-addin/src/v2/components/base/CellAddressPicker/types';
import Highland from 'highland';
import {
  ConstantConvertionError,
  ConstantsConversionsError,
  getMappedFieldNames,
  getRowIndexesToRead,
  PushError,
  ReadConvertionError,
} from './common';
import {
  ExecutorActionError,
  ExecutorActionEvent,
  ExecutorActionFinished,
  ExecutorActionStarted,
  ExecutorCancelled,
} from './events';
import Executor from './index';
import { AddressResolverFactory } from '../address-resolver-factory';

void Highland;

interface Dependencies {
  providerFactory: ProviderFactory;
  workbookApiFactory: WorkbookApiFactory;
  eventBus: EventBus;
  cellFormatter: CellFormatter;
  valueConverter: ValueConverter;
  addressResolverFactory: AddressResolverFactory;
}

class InsertExecutor extends Executor {
  private providerFactory: ProviderFactory;
  private workbookApiFactory: WorkbookApiFactory;
  private eventBus: EventBus;
  private cellFormatter: CellFormatter;
  private valueConverter: ValueConverter;
  private addressResolverFactory: AddressResolverFactory;

  constructor(deps: Dependencies) {
    super();
    this.providerFactory = deps.providerFactory;
    this.workbookApiFactory = deps.workbookApiFactory;
    this.eventBus = deps.eventBus;
    this.cellFormatter = deps.cellFormatter;
    this.valueConverter = deps.valueConverter;
    this.addressResolverFactory = deps.addressResolverFactory;
  }

  private timestamp: string | null = null;
  private resultErrors: InsertRecordError[] = [];
  private recordConvertionErrors: ReadConvertionError[] = [];
  private constantConvertionErrors: ConstantConvertionError[] = [];
  flowstep!: Flowstep;
  worksheet: any;
  private workbookApi!: WorkbookApi<any>;
  private addressResolver!: AddressResolver;
  private provider!: ProviderClient;
  private timezone!: string;
  private progressCounters = {
    total: 0,
    read: 0,
    processed: 0,
    populated: 0,
  };

  init(flowstep: Flowstep, context: any): void {
    this.flowstep = flowstep;
    this.timezone = context.timezone;
    this.workbookApi = this.workbookApiFactory(context);
    this.provider = this.providerFactory.getProviderClient(flowstep.provider, context);
    this.addressResolver = this.addressResolverFactory.getAddressResolver(flowstep, this.workbookApi);
    this.registerListeners();
  }

  private registerListeners() {
    this.eventBus.addListener<ProviderRemoteChunkEvent>(ProviderEvents.REMOTE_API_CHUNK, (event) => {
      this.progressCounters.processed += event.chunkSize;
      this.eventBus.notify(
        new ExecutorActionEvent(
          `Inserting`,
          `${this.progressCounters.processed} out of ${this.progressCounters.total}`,
        ),
      );
    });
  }

  private unregisterListeners() {
    this.eventBus.clearListeners(ProviderEvents.REMOTE_API_CHUNK);
  }

  async execute(cancelToken?: CancelToken) {
    try {
      this.eventBus.notify(new ExecutorActionStarted());
      this.flowstep = await this.addressResolver.replaceAddressesByValues(this.flowstep);

      const { worksheetId, mapping, target, onlyVisibleRows, rangeToApply, showOperationResultsTable } =
        this.flowstep.options;

      const { mappedHeaders, columnNameForResultIds, uniqueValuesColumn, constants } = mapping;
      this.eventBus.notify(new ExecutorActionEvent('Detecting data range'));
      this.worksheet = await this.workbookApi.getWorksheet?.(worksheetId);
      const range = await this.workbookApi.detectRangeByCell(worksheetId, target);

      if (!range) {
        throw new NoDataError('Could not find the range for update');
      }

      const providerFields = await this.provider.loadObjectFields(this.flowstep.providerOptions.objectType);

      const headers = await this.workbookApi.getColumnHeaders(range);
      const [fields, columnIndexes] = getMappedFieldNames(headers, mappedHeaders, providerFields);
      this.flowstep.providerOptions.columns = fields.map((field) => field.name);

      const uniqueColumnMapping = (mappedHeaders as MappedHeader[])?.find(
        (mappedHeader) => mappedHeader.excelColumnName === uniqueValuesColumn,
      );
      this.flowstep.providerOptions.uniqueFieldName = uniqueColumnMapping?.providerField?.name;
      this.flowstep.providerOptions.constants = this.convertConstantValues(constants, providerFields) || [];

      if (this.constantConvertionErrors.length) {
        throw new ConstantsConversionsError(this.constantConvertionErrors);
      }

      const options = {
        visibleOnly: onlyVisibleRows,
        columnsToRead: columnIndexes.length < headers.length ? columnIndexes : undefined,
        selectedOnly: rangeToApply.type === RangeType.Selected,
        rowSize: this.provider.estimateDataSize?.(fields),
        specifiedRowNumbers: await getRowIndexesToRead(this.flowstep, range, this.workbookApi),
      };

      const rowCount = await this.workbookApi.calcRowCountToRead(range, options);
      const dataStream = (await this.workbookApi.readRangeData(range, options))
        .map((values) => this.convertRowValues(values, fields, columnIndexes))
        .tap(() => this.updateReadCounter())
        .filter((values) => values !== null);

      this.progressCounters.total = rowCount;

      const providerStream = await this.provider.insertFlowstepData(this.flowstep, dataStream, cancelToken);

      let resultStream = this.processMetadataStream(providerStream);

      resultStream = this.collectResultErrors(resultStream);

      const idColumnIndex = headers.indexOf(columnNameForResultIds);

      resultStream = resultStream.tap(() => this.updatePopulatedCounter()).map((result) => this.formatResult(result));

      const resultRange = await this.workbookApi.writeRangeResult(range, resultStream, {
        idColumnIndex,
        idFormat: this.cellFormatter?.getIdFormat(),
        writeRowStates: showOperationResultsTable,
      });

      if (cancelToken?.isCanceled()) {
        const reason = cancelToken.reason || 'Execution cancelled';
        this.eventBus.notify(new ExecutorCancelled(reason));
        return { error: new CancelError(reason) };
      }

      if (this.resultErrors.length > 0 || this.recordConvertionErrors.length > 0) {
        await this.throwPushErrors(range, resultRange);
      }

      this.eventBus.notify(
        new ExecutorActionFinished('Execution finished', { resultSuccessRange: resultRange, warnings: [] }),
      );
      return { warnings: [] };
    } catch (error: unknown) {
      if (error instanceof NoDataError) {
        this.eventBus.notify(new ExecutorActionFinished('Execution finished with warnings', { warnings: [error] }));
        return {};
      }
      if (error instanceof CancelError) {
        this.eventBus.notify(new ExecutorCancelled(error.message));
        return { error };
      }
      this.eventBus.notify(new ExecutorActionError(error as Error));
      return { warnings: [], error };
    } finally {
      this.unregisterListeners();
      if (this.flowstep.options.timestampCell) {
        await this.setLastRunTimestamp();
      }
    }
  }

  private processMetadataStream(stream: Highland.Stream<any>): Highland.Stream<any> {
    return stream.filter((item: any) => {
      if (!isMetadata(item)) {
        return true;
      }

      this.processMetaItem(item);
      return false;
    });
  }

  private processMetaItem(item: MetadataResult): void {
    if (item.type === STREAM_METADATA.TIMESTAMP) {
      this.timestamp = item.payload.timestamp;
    }
  }

  private collectResultErrors(stream: Highland.Stream<any>) {
    return stream.tap((item: any) => {
      if (isInsertRecordError(item)) {
        this.resultErrors.push(item);
      }
    });
  }

  private async setLastRunTimestamp() {
    if (!this.timestamp) {
      return;
    }
    const { worksheetId, address } = this.flowstep.options.timestampCell as CellAddressPickerValue;
    const range = await this.workbookApi.getRangeByAddress(`${worksheetId}!${address}`);
    if (!range) {
      return;
    }
    await this.workbookApi.writeRangeValue(range, [[this.timestamp]]);
  }

  private updateReadCounter() {
    this.progressCounters.read++;
    if (this.progressCounters.read % 1000 === 0) {
      this.eventBus.notify(
        new ExecutorActionEvent('Reading data', `${this.progressCounters.read} out of ${this.progressCounters.total}`),
      );
    }
  }

  private updatePopulatedCounter() {
    this.progressCounters.populated++;
    if (this.progressCounters.populated % 1000 === 0) {
      this.eventBus.notify(
        new ExecutorActionEvent(
          `Populating results`,
          `${this.progressCounters.populated} out of ${this.progressCounters.total}`,
        ),
      );
    }
  }

  private async throwPushErrors(tableRange: any, resultRange?: any) {
    const { address } = await this.workbookApi.getRangeAddressInfo(tableRange);
    const rangeAddress = RangeAddress.fromString(address);
    let resultError;
    let convertionError;

    if (this.resultErrors.length) {
      resultError = await this.createResultError(rangeAddress, resultRange);
    }
    if (this.recordConvertionErrors.length) {
      convertionError = this.createConvertionError(rangeAddress);
    }

    throw new PushError(resultError, convertionError);
  }

  private async createResultError(tableAddress: RangeAddress, resultRange: any) {
    const messages = this.resultErrors.map(
      (error) => `Row${tableAddress.startRow + error.__rowIndex}: Error inserting ${error.status}`,
    );

    return new ResultDataError(messages, resultRange);
  }

  private createConvertionError(tableAddress: RangeAddress) {
    return new ConversionError(
      this.recordConvertionErrors.map((error) => ({ ...error, sheet: tableAddress.sheetName })),
    );
  }

  private convertRowValues(rowValues: PushObjectRecord, fields: DataField[], columnIndexes: number[]) {
    if (!Array.isArray(rowValues)) {
      return rowValues;
    }

    let hasConversionErrorOccurred = false;

    const convertedRow = rowValues.map((value, index) => {
      //rowIndex
      if (index === 0) {
        return value;
      }

      const { value: convertedValue, error } = this.valueConverter.toProviderValue(value, fields[index - 1], {
        timezone: this.timezone,
      });
      if (error) {
        hasConversionErrorOccurred = true;
        this.recordConvertionErrors.push({
          error,
          rowIndex: rowValues[0],
          columnIndex: columnIndexes[index - 1],
        });
        return;
      }

      return convertedValue;
    });

    return hasConversionErrorOccurred ? null : (convertedRow as PushObjectRecord);
  }

  private convertConstantValues(constants: ConstantField[], fields: DataField[]) {
    return constants.map((constant) => {
      const field = fields.find((f) => f.name === constant.field.name);
      if (!field) {
        this.constantConvertionErrors.push({
          error: 'Field not found',
          fieldName: constant.field.name,
          value: constant.value,
        });
      }
      const { value: convertedValue, error } = this.valueConverter.toProviderValue(constant.value, field!, {
        timezone: this.timezone,
      });
      if (error) {
        this.constantConvertionErrors.push({
          error,
          fieldName: constant.field.name,
          value: constant.value,
        });
      }
      return {
        ...constant,
        value: convertedValue,
      };
    });
  }

  private formatResult(result: UpdateResult) {
    const timestamp = this.timestamp ? dayjs(this.timestamp).format('YYYY-MM-DD HH:mm:ss') : '';
    const status = result.status === 'success' ? 'Successfully inserted' : result.status.replace('\n', ' ');
    return { ...result, status, timestamp };
  }
}

export default InsertExecutor;
